import { WebStorageStateStore } from "oidc-client-ts";

/**
 * Hvor BankID-innloggingen (OIDC-brukeren) lagres — én kilde for alle som
 * leser eller skriver den: `userStore` i App.tsx, `readOidcSub()`,
 * dev-innloggingen og fanesynkroniseringen under.
 *
 * localStorage, slik at innloggingen gjelder i alle faner og en kopiert lenke
 * kan åpnes i en ny fane. Det betyr også at den overlever at nettleseren
 * lukkes — derfor `purgeExpiredOidcUser()`.
 *
 * API-et identifiserer søkeren på `sub` alene og validerer ikke tokenet. Uten
 * utløpssjekken her ville en lagret innlogging derfor gjelde til noen trykket
 * «Logg ut», også på delte maskiner.
 */
const storage = window.localStorage;

export const OIDC_USER_KEY = `oidc.user:${import.meta.env.VITE_OIDC_AUTHORITY}:${import.meta.env.VITE_OIDC_CLIENT_ID}`;

export const oidcUserStore = new WebStorageStateStore({ store: storage });

type StoredOidcUser = { profile?: { sub?: string }; expires_at?: number };

const parse = (raw: string | null): StoredOidcUser | null => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

/** `sub` til den lagrede brukeren, eller tom streng. Leses synkront. */
export function readStoredSub(): string {
    const sub = parse(storage.getItem(OIDC_USER_KEY))?.profile?.sub;
    return typeof sub === 'string' ? sub : '';
}

/** Lagrer en bruker der oidc-client-ts leter (brukes av dev-innloggingen). */
export function writeStoredUser(user: object): void {
    storage.setItem(OIDC_USER_KEY, JSON.stringify(user));
}

/**
 * Fjerner en lagret innlogging der tokenet er utløpt. Kjøres ved oppstart,
 * før noe leser brukeren: den som åpner portalen etter at tokenet er utløpt,
 * må logge inn på nytt.
 *
 * Bevisst bare ved oppstart: utløper tokenet mens søkeren holder på, får hen
 * fortsette, i stedet for å bli kastet ut midt i et skjema som ikke er lagret.
 */
export function purgeExpiredOidcUser(): void {
    const expiresAt = parse(storage.getItem(OIDC_USER_KEY))?.expires_at;
    if (typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now()) {
        storage.removeItem(OIDC_USER_KEY);
    }
}

/**
 * Laster fanen på nytt når innloggingen endres i en annen fane: utlogging,
 * innlogging, eller at en annen person logger inn. Uten dette fortsetter
 * fanen med forrige persons `sub` i minnet, mens `readOidcSub()` leser den
 * nye — og kall fra samme fane kan da gå på to ulike personer.
 *
 * Fornyet token for samme person gir ingen reload. `storage`-eventen fyrer
 * bare i de andre fanene, aldri i den som gjorde endringen.
 */
export function reloadOnLoginChangeInOtherTab(): void {
    window.addEventListener('storage', (event) => {
        if (event.storageArea !== storage || event.key !== OIDC_USER_KEY) return;

        const before = parse(event.oldValue)?.profile?.sub ?? '';
        const after = parse(event.newValue)?.profile?.sub ?? '';
        if (before !== after) window.location.reload();
    });
}
